import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, User, ArrowLeft, Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { adminApi, setToken } from '../lib/api';

const AdminLogin = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.login(username, password);
      setToken(data.access_token);
      onSuccess(data.username);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 flex items-center justify-center p-6">
      {/* Decorative diamonds */}
      <div className="absolute top-20 left-20 w-24 h-24 rotate-45 bg-[#F4A340]/10 rounded-2xl blur-2xl" />
      <div className="absolute bottom-20 right-20 w-32 h-32 rotate-45 bg-[#F4A340]/20 rounded-3xl blur-3xl" />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-[#E89534] transition-colors mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </Link>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#F4A340] to-[#E89534] px-8 py-8 text-center relative overflow-hidden">
            <div className="absolute -top-8 -right-8 w-32 h-32 rotate-45 bg-white/10 rounded-3xl" />
            <div className="absolute -bottom-8 -left-8 w-24 h-24 rotate-45 bg-white/10 rounded-2xl" />
            <div className="relative">
              <div className="w-14 h-14 mx-auto mb-3 rotate-45 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <span className="-rotate-45 text-[#E89534] font-bold text-2xl italic">p</span>
              </div>
              <h1 className="text-2xl font-bold text-white">Espace Administrateur</h1>
              <p className="text-white/90 text-sm mt-1">Pierre &amp; Edois · OLS</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="p-8 space-y-5">
            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
              <ShieldCheck className="w-4 h-4 flex-shrink-0" />
              <span>Connexion sécurisée · Session valide 12h</span>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Nom d'utilisateur</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="olsAdmin"
                  autoComplete="username"
                  className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none transition-colors"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full pl-11 pr-12 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-in slide-in-from-top-1 duration-300">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 disabled:cursor-not-allowed text-white font-semibold rounded-full shadow-md hover:shadow-lg transition-all duration-300"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Connexion...
                </>
              ) : (
                <>Se connecter</>
              )}
            </button>
          </form>

          <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 text-center text-xs text-slate-500">
            Accès réservé au personnel autorisé
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
